import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box, FormControl, Divider, Button,
  RadioGroup, FormControlLabel, Radio, ToggleButtonGroup,
  ToggleButton,
} from '@mui/material';
import * as xlsx from 'xlsx';
import dayjs from 'dayjs';
import AskDialog from '../../components/common/dialog/AskDialog';
import CommonDatePicker from '../../components/common/util/CommonDatePicker';
import SalesresultsStaticsHqContainer from './SalesresultsStaticsHqContainer';
import SalesresultsStaticsTeamContainer from './SalesresultsStaticsTeamContainer';
import SalesresultsStaticsEmp from './SalesresultsStaticsEmp';

const SalesresultsStaticsContainer = () => {
  const hqTableRef = useRef();
  const dpTableRef = useRef();
  const empTableRef = useRef();

  const [searchStrtDt, setSearchStrtDt] = useState(dayjs().startOf('year'));
  const [searchEndDt, setSearchEndDt] = useState(dayjs()); 
  const [searchPrjTyp, setSearchPrjTyp] = useState('A');
  const [type, setType] = useState('headquarter');
  const [searchData, setSearchData] = useState(null);
  const [visible, setVisible] = useState(false);

  // Formik 더미 데이터 (컴포넌트 props 호환용)
  const mockFormik = { touched: {}, errors: {}, values: {} };

  // 검색 키 생성 (이 값이 변해야 하위 API 호출)
  const updateSearchData = useCallback(() => {
    const start = searchStrtDt.format('YYYY-MM-DD');
    const end = searchEndDt.format('YYYY-MM-DD');
    setSearchData(`${type}_${searchPrjTyp}_${start}~${end}`);
  }, [type, searchPrjTyp, searchStrtDt, searchEndDt]);

  // 검색 버튼 클릭
  const searchClickHandler = () => {
    if (searchStrtDt.isAfter(searchEndDt, 'day')) {
      setVisible(true);
      return;
    }
    updateSearchData();
  };

  // 초기 로드 시 1회 실행
  useEffect(() => {
    updateSearchData();
  }, []);

  // 엑셀 다운로드 로직
  const handleDownExcel = () => {
    const refs = { headquarter: hqTableRef, depart: dpTableRef, employee: empTableRef };
    const titles = { headquarter: '본부', depart: '팀', employee: '팀원' };
    
    const targetRef = refs[type].current;
    if (targetRef) {
      const wb = xlsx.utils.table_to_book(targetRef, { sheet: 'sheet1', raw: true });
      xlsx.writeFile(wb, `${titles[type]}_매출통계_${dayjs().format('YYYYMMDD')}.xlsx`);
    }
  };

  // 하위 컨테이너 Props 생성 함수
  const getSubContainerProps = (ref) => {
    const start = dayjs(searchStrtDt);
    const end = dayjs(searchEndDt);

    return {
      type,
      selectedYear: start.format('YYYY'),
      strMonth: start.format('MM'),
      strDay: start.format('DD'),
      endMonth: end.format('MM'),
      endDay: end.format('DD'),
      tableRef: ref,
      searchData,
      selectPrjTyp: searchPrjTyp,
    };
  };

  const handleDateChange = (name, newValue) => {
    const date = dayjs(newValue);
    if (name === 'searchStrtDt') setSearchStrtDt(date);
    else setSearchEndDt(date);
  };

useEffect(() => {
  if (!searchStrtDt || !searchEndDt || !searchStrtDt.isValid() || !searchEndDt.isValid()) {
    return;
  }

  const isDifferentYear = searchStrtDt.year() !== searchEndDt.year();
  if (isDifferentYear) {
    return; 
  }

  const isInvalidDate = searchStrtDt.isAfter(searchEndDt, 'day');
  if (isInvalidDate) {
    return;
  }

  updateSearchData();

}, [searchStrtDt, searchEndDt, updateSearchData]);



  return (
    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
      <Box 
        sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          mb: 1, 
          flexWrap: 'wrap',
          gap: 1.5, 
          my: 2 
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <CommonDatePicker
            label="매출 시작일"
            name="searchStrtDt"
            value={searchStrtDt}
            formik={mockFormik}
            onChange={(val) => handleDateChange('searchStrtDt', val)}
            sx={{ width: { xs: '15ch', md: '17ch' } }} 
          />
          <CommonDatePicker
            label="매출 종료일"
            name="searchEndDt"
            value={searchEndDt}
            formik={mockFormik}
            onChange={(val) => handleDateChange('searchEndDt', val)}
            sx={{ width: { xs: '15ch', md: '17ch' } }}
          />
        </Box>

        <FormControl>
          <RadioGroup 
            row 
            value={searchPrjTyp} 
            onChange={(e) => setSearchPrjTyp(e.target.value)}
          >
            <FormControlLabel value="A" control={<Radio size="small" />} label="전체" />
            <FormControlLabel value="N" control={<Radio size="small" />} label="프로젝트" />
            <FormControlLabel value="M" control={<Radio size="small" />} label="유지보수" />
            <FormControlLabel value="ETC" control={<Radio size="small" />} label="기타" />
          </RadioGroup>
        </FormControl>

        <ToggleButtonGroup 
          size="small" 
          color="primary" 
          value={type} 
          exclusive 
          onChange={(_, val) => val && setType(val)}
        >
          <ToggleButton value="headquarter">본부</ToggleButton>
          <ToggleButton value="depart">팀</ToggleButton>
          <ToggleButton value="employee">팀원</ToggleButton>
        </ToggleButtonGroup>

        <Box sx={{ flexGrow: 1 }} />

        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button variant="contained" color="info" onClick={searchClickHandler}>조회</Button>
          <Button variant="contained" color="success" onClick={handleDownExcel}>Excel</Button>
        </Box>
      </Box>

      <Box sx={{ mt: 2 }}>
        {type === 'headquarter' && <SalesresultsStaticsHqContainer {...getSubContainerProps(hqTableRef)} />}
        {type === 'depart' && <SalesresultsStaticsTeamContainer {...getSubContainerProps(dpTableRef)} />}
        {type === 'employee' && <SalesresultsStaticsEmp {...getSubContainerProps(empTableRef)} />}
      </Box>

      <AskDialog
        visible={visible}
        title="조회 기간 오류"
        description="시작일이 종료일보다 늦을 수 없습니다. 기간을 다시 설정해주세요."
        onConfirm={() => setVisible(false)}
        onCancle={() => setVisible(false)}
      />
    </Box>
  );
};

export default SalesresultsStaticsContainer;
